import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ExpiryItem,
  HistoryEvent,
  useUsers,
  useItemHistory,
  MockUser,
  useUploadDocument,
  AIExtractionResult,
} from "../hooks/useExpiryItems";
import { useAuth } from "../hooks/useAuth";
import { usePlan } from "../hooks/usePlan";
import { PlanBadge, UpgradeModal } from "./UpgradePrompt";
import type { PlanLimits } from "../config/plans.config";
import { format } from "date-fns";
import HistoryTimeline from "./HistoryTimeline";

// ── Predefined document categories ───────────────────────────────────────────
export const DOCUMENT_CATEGORIES = [
  "Compliance",
  "Contracts",
  "Certifications",
  "Finance",
  "HR",
  "Health & Safety",
  "Insurance",
  "Legal",
  "Licenses & Permits",
  "Operations",
  "Real Estate",
  "Tax",
  "Vendor",
  "Others",
] as const;

const isKnownCategory = (val: string) =>
  DOCUMENT_CATEGORIES.includes(val as any);

// ── Shared styles ─────────────────────────────────────────────────────────────
const fieldCls =
  "mt-1 block w-full border border-[#E5E7EB] rounded-md py-2 px-3 text-sm text-[#111827] bg-white placeholder:text-[#9CA3AF] transition-colors appearance-none focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED]";
const aiFieldCls =
  "mt-1 block w-full border border-[#7C3AED] rounded-md py-2 px-3 text-sm text-[#111827] bg-[#FDFCFF] placeholder:text-[#9CA3AF] transition-colors appearance-none focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20";
const disabledFieldCls =
  "mt-1 block w-full border border-[#E5E7EB] rounded-md py-2 px-3 text-sm text-[#6B7280] bg-[#F9FAFB] cursor-not-allowed appearance-none";

// ── AI badge ──────────────────────────────────────────────────────────────────
const AiBadge = () => (
  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#F5F3FF] border border-[#DDD6FE] text-[#7C3AED] text-[9px] font-bold tracking-wide ml-1.5">
    <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" />
    </svg>
    AI
  </span>
);

// ── Confidence pill ───────────────────────────────────────────────────────────
const CONFIDENCE_STYLES: Record<AIExtractionResult["confidence"], string> = {
  high: "bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]",
  medium: "bg-[#FEF9C3] text-[#92400E] border-[#FDE68A]",
  low: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]",
};

// ── Owner chip input ──────────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface OwnerChipInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  users: MockUser[];
  /** Called when user tries to add an external (unregistered) email but plan blocks it */
  onExternalBlocked?: () => void;
  /** Whether adding external (unregistered) emails is allowed */
  allowExternalEmails?: boolean;
}

export const OwnerChipInput: React.FC<OwnerChipInputProps> = ({
  value,
  onChange,
  users,
  onExternalBlocked,
  allowExternalEmails = true,
}) => {
  const [inputVal, setInputVal] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const suggestions = users.filter(
    (u) =>
      !value.includes(u.email) &&
      (u.email.toLowerCase().includes(inputVal.toLowerCase()) ||
        u.name.toLowerCase().includes(inputVal.toLowerCase()))
  );

  const addEmail = useCallback(
    (email: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed || !EMAIL_RE.test(trimmed) || value.includes(trimmed)) {
        setInputVal("");
        setShowSuggestions(false);
        return;
      }
      // Check if this is an external (unregistered) email
      const isRegistered = users.some((u) => u.email === trimmed);
      if (!isRegistered && !allowExternalEmails) {
        onExternalBlocked?.();
        setInputVal("");
        setShowSuggestions(false);
        return;
      }
      onChange([...value, trimmed]);
      setInputVal("");
      setShowSuggestions(false);
    },
    [value, onChange, users, allowExternalEmails, onExternalBlocked]
  );

  const removeEmail = (email: string) =>
    onChange(value.filter((e) => e !== email));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault();
      if (inputVal) addEmail(inputVal);
    } else if (e.key === "Backspace" && !inputVal && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const getDisplayName = (email: string) => {
    const u = users.find((u) => u.email === email);
    return u?.name || email;
  };

  const getInitials = (email: string) => {
    const u = users.find((u) => u.email === email);
    if (u?.name)
      return u.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    return email[0].toUpperCase();
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className="min-h-[38px] w-full border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white flex flex-wrap gap-1.5 items-center cursor-text focus-within:ring-2 focus-within:ring-[#7C3AED]/20 focus-within:border-[#7C3AED] transition-colors"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1.5 pl-1 pr-1.5 py-0.5 rounded-full bg-[#EFF6FF] border border-[#BFDBFE] text-xs font-medium text-[#1E40AF] max-w-[200px]"
          >
            <span className="h-4 w-4 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
              {getInitials(email)}
            </span>
            <span className="truncate">{getDisplayName(email)}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeEmail(email);
              }}
              className="text-[#93C5FD] hover:text-[#1D4ED8] transition-colors flex-shrink-0"
            >
              <svg
                className="h-3 w-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={(e) => {
            setInputVal(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            setTimeout(() => {
              if (inputVal) addEmail(inputVal);
              setShowSuggestions(false);
            }, 150);
          }}
          placeholder={value.length === 0 ? "Type email or name…" : ""}
          className="flex-1 min-w-[140px] text-sm text-[#111827] placeholder:text-[#9CA3AF] bg-transparent border-none outline-none py-0.5"
        />
      </div>
      {value.length === 0 && (
        <p className="mt-1 text-xs text-[#9CA3AF]">
          Press Enter or comma to add multiple owners
        </p>
      )}
      {showSuggestions && inputVal && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg overflow-hidden">
          {suggestions.slice(0, 5).map((u) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addEmail(u.email)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[#EFF6FF] transition-colors"
            >
              <span className="h-7 w-7 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                {u.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#111827] truncate">
                  {u.name}
                </p>
                <p className="text-xs text-[#9CA3AF] truncate">{u.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Document Scan Zone ────────────────────────────────────────────────────────
type ScanState =
  | { status: "idle" }
  | { status: "uploading"; filename: string }
  | { status: "done"; result: AIExtractionResult }
  | { status: "error"; message: string };

const ACCEPTED_MIME =
  "application/pdf,text/plain,image/png,image/jpeg,image/jpg,image/webp,image/tiff";

interface DocumentScanZoneProps {
  onScanComplete: (result: AIExtractionResult) => void;
}

const DocumentScanZone: React.FC<DocumentScanZoneProps> = ({
  onScanComplete,
}) => {
  const [scanState, setScanState] = useState<ScanState>({ status: "idle" });
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadDocument();

  const processFile = useCallback(
    async (file: File) => {
      setScanState({ status: "uploading", filename: file.name });
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await uploadMutation.mutateAsync(formData);
        setScanState({ status: "done", result: res.extracted });
      } catch (err: any) {
        const msg =
          err?.response?.data?.error || err?.message || "Upload failed";
        setScanState({ status: "error", message: msg });
      }
    },
    [uploadMutation]
  );

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      processFile(files[0]);
    },
    [processFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  // Count how many fields were found
  const fieldsFound =
    scanState.status === "done"
      ? [
          scanState.result.document_name,
          scanState.result.category,
          scanState.result.expiry_date,
          scanState.result.notes,
        ].filter(Boolean).length
      : 0;

  return (
    <div className="rounded-xl border border-[#EDE9FE] bg-gradient-to-br from-[#FDFCFF] to-[#F5F3FF] p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-[#7C3AED] flex items-center justify-center">
            <svg
              className="h-4 w-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <div>
            <p className="text-xs font-semibold text-[#5B21B6]">
              AI Document Scanner
            </p>
            <p className="text-[10px] text-[#8B5CF6]">
              Upload a file to auto-fill all fields
            </p>
          </div>
        </div>
        {scanState.status === "done" && (
          <button
            type="button"
            onClick={() => setScanState({ status: "idle" })}
            className="text-[10px] text-[#8B5CF6] hover:text-[#5B21B6] font-medium underline transition-colors"
          >
            Scan another
          </button>
        )}
      </div>

      {/* ── Idle: drop zone ── */}
      {scanState.status === "idle" && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-2 py-5 rounded-lg border-2 border-dashed cursor-pointer transition-all ${
            isDragging
              ? "border-[#7C3AED] bg-[#EDE9FE] scale-[1.01]"
              : "border-[#C4B5FD] bg-white hover:border-[#7C3AED] hover:bg-[#EDE9FE]"
          }`}
        >
          <svg
            className="h-9 w-9 text-[#A78BFA]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <div className="text-center">
            <p className="text-xs font-medium text-[#5B21B6]">
              Drop your document here
            </p>
            <p className="text-[10px] text-[#8B5CF6] mt-0.5">
              PDF · PNG · JPG · WEBP · TIFF · TXT — AI fills Name, Category,
              Expiry & Notes
            </p>
          </div>
          <span className="text-[10px] font-semibold text-[#7C3AED] bg-white border border-[#DDD6FE] px-3 py-1 rounded-full">
            Click to browse
          </span>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_MIME}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      )}

      {/* ── Uploading ── */}
      {scanState.status === "uploading" && (
        <div className="flex flex-col items-center gap-3 py-5">
          <div className="relative h-12 w-12">
            <div className="absolute inset-0 rounded-full border-4 border-[#EDE9FE]" />
            <div className="absolute inset-0 rounded-full border-4 border-t-[#7C3AED] animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="h-5 w-5 text-[#7C3AED]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs font-semibold text-[#5B21B6]">
              AI is reading your document…
            </p>
            <p className="text-[10px] text-[#8B5CF6] mt-0.5 truncate max-w-[220px]">
              {scanState.filename}
            </p>
          </div>
          <div className="flex gap-3 text-[10px] text-[#8B5CF6]">
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7C3AED] animate-pulse" />
              Extracting text
            </span>
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#A78BFA] animate-pulse delay-150" />
              Analysing fields
            </span>
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C4B5FD] animate-pulse delay-300" />
              Filling form
            </span>
          </div>
        </div>
      )}

      {/* ── Done ── */}
      {scanState.status === "done" && (
        <div className="space-y-3">
          {/* Summary row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 rounded-full bg-[#7C3AED] flex items-center justify-center flex-shrink-0">
                <svg
                  className="h-3 w-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-xs font-semibold text-[#5B21B6]">
                {fieldsFound > 0
                  ? `${fieldsFound} field${fieldsFound !== 1 ? "s" : ""} detected`
                  : "Scan complete"}
              </p>
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${CONFIDENCE_STYLES[scanState.result.confidence]}`}
              >
                {scanState.result.confidence}
              </span>
            </div>
          </div>

          {/* Detected fields preview */}
          {fieldsFound > 0 && (
            <div className="bg-white rounded-lg border border-[#EDE9FE] divide-y divide-[#F5F3FF]">
              {scanState.result.document_name && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="text-[10px] font-semibold text-[#9CA3AF] w-14 flex-shrink-0">
                    NAME
                  </span>
                  <span className="text-xs text-[#111827] truncate font-medium">
                    {scanState.result.document_name}
                  </span>
                </div>
              )}
              {scanState.result.category && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="text-[10px] font-semibold text-[#9CA3AF] w-14 flex-shrink-0">
                    CATEGORY
                  </span>
                  <span className="text-xs text-[#111827] truncate">
                    {scanState.result.category}
                  </span>
                </div>
              )}
              {scanState.result.expiry_date && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="text-[10px] font-semibold text-[#9CA3AF] w-14 flex-shrink-0">
                    EXPIRY
                  </span>
                  <span className="text-xs text-[#111827] font-medium">
                    {format(
                      new Date(scanState.result.expiry_date),
                      "MMM dd, yyyy"
                    )}
                  </span>
                </div>
              )}
              {scanState.result.notes && (
                <div className="flex items-start gap-2 px-3 py-2">
                  <span className="text-[10px] font-semibold text-[#9CA3AF] w-14 flex-shrink-0 pt-0.5">
                    NOTES
                  </span>
                  <span className="text-xs text-[#6B7280] line-clamp-2">
                    {scanState.result.notes}
                  </span>
                </div>
              )}
            </div>
          )}

          {fieldsFound === 0 && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#FFFBEB] border border-[#FDE68A]">
              <svg
                className="h-4 w-4 text-[#D97706] flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-[#92400E]">
                {scanState.result.reasoning ||
                  "No fields detected — please fill in manually."}
              </p>
            </div>
          )}

          {/* Apply button */}
          {fieldsFound > 0 && (
            <button
              type="button"
              onClick={() => onScanComplete(scanState.result)}
              className="w-full flex items-center justify-center gap-2 py-2 text-xs font-semibold text-white bg-[#7C3AED] hover:bg-[#6D28D9] rounded-lg transition-colors shadow-sm"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Apply {fieldsFound} detected field{fieldsFound !== 1 ? "s" : ""}{" "}
              to form
            </button>
          )}
        </div>
      )}

      {/* ── Error ── */}
      {scanState.status === "error" && (
        <div className="flex items-start gap-2 px-3 py-3 rounded-lg border border-[#FECACA] bg-[#FEF2F2]">
          <svg
            className="h-4 w-4 text-[#DC2626] flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-xs font-medium text-[#B91C1C]">Scan failed</p>
            <p className="text-[10px] text-[#B91C1C] mt-0.5 break-words">
              {scanState.message}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setScanState({ status: "idle" })}
            className="text-[10px] text-[#9CA3AF] hover:text-[#374151] transition-colors flex-shrink-0 mt-0.5"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main Modal ────────────────────────────────────────────────────────────────
interface ItemModalProps {
  item: ExpiryItem | null;
  onClose: () => void;
  onSubmit: (item: any) => Promise<void>;
  initialRenewalMode?: boolean;
  focusExpiryDate?: boolean;
}

type Tab = "details" | "history";

const ItemModal: React.FC<ItemModalProps> = ({
  item,
  onClose,
  onSubmit,
  initialRenewalMode = false,
  focusExpiryDate = false,
}) => {
  const isEditing = !!item;
  const { user: currentUser } = useAuth();
  const { canUse, requiredPlanFor } = usePlan();
  const { data: users = [] } = useUsers();

  const [tab, setTab] = useState<Tab>("details");
  const [historyTabOpened, setHistoryTabOpened] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState("");
  const [categorySelect, setCategorySelect] = useState("");
  const [categoryCustom, setCategoryCustom] = useState("");
  const [ownerEmails, setOwnerEmails] = useState<string[]>([]);
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  // Track which fields were filled by AI (for badges)
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());

  // Renewal / edit mode state
  const [isRenewalMode, setIsRenewalMode] = useState(false);
  const [isDirectEditMode, setIsDirectEditMode] = useState(false);
  const expiryDateRef = useRef<HTMLInputElement>(null);

  const isOthers = categorySelect === "__others__";

  // ── Populate form when editing ───────────────────────────────────────────
  useEffect(() => {
    setTab("details");
    setHistoryTabOpened(false);
    setIsRenewalMode(initialRenewalMode);
    setIsDirectEditMode(false);
    setAiFilledFields(new Set());
    if (item) {
      setName(item.name);
      if (isKnownCategory(item.category)) {
        setCategorySelect(item.category);
        setCategoryCustom("");
      } else {
        setCategorySelect("__others__");
        setCategoryCustom(item.category || "");
      }
      setExpiryDate(
        item.expiry_date ? format(new Date(item.expiry_date), "yyyy-MM-dd") : ""
      );
      setOwnerEmails(
        (item.owners ?? []).map((o: any) => o.email).filter(Boolean)
      );
      setNotes(item.notes || "");
    } else {
      setName("");
      setCategorySelect("");
      setCategoryCustom("");
      // Pre-fill owner with the logged-in user's email — they can remove it
      setOwnerEmails(currentUser?.email ? [currentUser.email] : []);
      setExpiryDate("");
      setNotes("");
    }
    if (focusExpiryDate) setTimeout(() => expiryDateRef.current?.focus(), 100);
  }, [item, initialRenewalMode, focusExpiryDate, currentUser]);

  // ── AI scan complete → fill all detected fields ───────────────────────────
  const handleScanComplete = useCallback(
    (result: AIExtractionResult) => {
      const filled = new Set<string>();

      if (result.document_name) {
        setName(result.document_name);
        filled.add("name");
      }
      if (result.category && isKnownCategory(result.category)) {
        setCategorySelect(result.category);
        setCategoryCustom("");
        filled.add("category");
      }
      if (result.expiry_date) {
        setExpiryDate(result.expiry_date);
        filled.add("expiry");
        // In edit mode, unlock the date field so the filled value is visible
        if (isEditing && !isRenewalMode) setIsDirectEditMode(true);
      }
      if (result.notes) {
        setNotes(result.notes);
        filled.add("notes");
      }
      setAiFilledFields(filled);
    },
    [isEditing, isRenewalMode]
  );

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const finalCategory = isOthers ? categoryCustom.trim() : categorySelect;
    await onSubmit({
      name,
      category: finalCategory,
      owner_emails: ownerEmails,
      expiry_date: expiryDate || null,
      notes: notes || undefined,
      ...(isRenewalMode ? { isRenewal: true } : {}),
      ...(isDirectEditMode ? { isExpiryEdit: true } : {}),
    });
    setLoading(false);
  };

  const { data: fetchedHistory, isLoading: historyLoading } = useItemHistory(
    item?.id ?? "",
    historyTabOpened && isEditing
  );
  const historyEvents: HistoryEvent[] = fetchedHistory ?? item?.history ?? [];

  // Helper: label with optional AI badge
  const FieldLabel = ({
    htmlFor,
    label,
    required,
    aiKey,
  }: {
    htmlFor?: string;
    label: string;
    required?: boolean;
    aiKey: string;
  }) => (
    <label
      htmlFor={htmlFor}
      className="flex items-center text-xs font-medium text-[#374151] mb-1"
    >
      {label}
      {required && <span className="text-[#DC2626] ml-0.5">*</span>}
      {aiFilledFields.has(aiKey) && <AiBadge />}
    </label>
  );

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-[#111827] bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Panel — wider to fit scanner + form comfortably */}
        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
          {/* ── Modal Header ──────────────────────────────────────────────── */}
          <div className="px-6 pt-5 pb-0 border-b border-[#E5E7EB]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-[#111827]">
                  {isEditing ? "Edit Document" : "Add New Document"}
                </h3>
                {!isEditing && (
                  <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                    Scan a document to auto-fill, or fill in manually below.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-md text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6] transition-colors"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Tabs — only in edit mode */}
            {isEditing && (
              <div className="flex gap-1">
                {(["details", "history"] as Tab[]).map((t) => {
                  const isHistoryLocked =
                    t === "history" && !canUse("auditTrail");
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        if (isHistoryLocked) {
                          setUpgradeFeature("auditTrail");
                          return;
                        }
                        setTab(t);
                        if (t === "history") setHistoryTabOpened(true);
                      }}
                      title={
                        isHistoryLocked
                          ? `Audit trail requires ${requiredPlanFor("auditTrail")} plan`
                          : undefined
                      }
                      className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
                        tab === t
                          ? "border-[#2563EB] text-[#2563EB]"
                          : "border-transparent text-[#6B7280] hover:text-[#374151]"
                      } ${isHistoryLocked ? "opacity-60" : ""}`}
                    >
                      {t}
                      {isHistoryLocked && (
                        <PlanBadge
                          requiredPlan={requiredPlanFor("auditTrail")}
                        />
                      )}
                      {t === "history" &&
                        !isHistoryLocked &&
                        !historyLoading &&
                        historyEvents.length > 0 && (
                          <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-[#EFF6FF] text-[#2563EB] font-semibold">
                            {historyEvents.length}
                          </span>
                        )}
                      {t === "history" &&
                        !isHistoryLocked &&
                        historyLoading && (
                          <span className="ml-1.5 inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent align-middle" />
                        )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Tab: Details ──────────────────────────────────────────────── */}
          {tab === "details" && (
            <form onSubmit={handleSubmit}>
              <div className="px-6 pt-5 pb-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* ── AI Scanner (create mode: prominent; edit mode: compact) ── */}
                <DocumentScanZone onScanComplete={handleScanComplete} />

                {/* AI-fill notice */}
                {aiFilledFields.size > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F5F3FF] border border-[#DDD6FE]">
                    <svg
                      className="h-3.5 w-3.5 text-[#7C3AED] flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" />
                    </svg>
                    <p className="text-[10px] text-[#5B21B6] font-medium">
                      {aiFilledFields.size} field
                      {aiFilledFields.size !== 1 ? "s" : ""} auto-filled by AI —
                      review and edit as needed.
                    </p>
                    <button
                      type="button"
                      onClick={() => setAiFilledFields(new Set())}
                      className="ml-auto text-[10px] text-[#8B5CF6] hover:text-[#5B21B6] transition-colors flex-shrink-0"
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {/* ── Name ── */}
                <div>
                  <FieldLabel
                    htmlFor="modal-name"
                    label="Name"
                    required
                    aiKey="name"
                  />
                  <input
                    type="text"
                    id="modal-name"
                    required
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setAiFilledFields((p) => {
                        const n = new Set(p);
                        n.delete("name");
                        return n;
                      });
                    }}
                    placeholder="e.g. Trade License"
                    className={
                      aiFilledFields.has("name") ? aiFieldCls : fieldCls
                    }
                  />
                </div>

                {/* ── Category ── */}
                <div>
                  <FieldLabel
                    htmlFor="modal-category"
                    label="Category"
                    required
                    aiKey="category"
                  />
                  <div className="relative">
                    <select
                      id="modal-category"
                      required
                      value={categorySelect}
                      onChange={(e) => {
                        setCategorySelect(e.target.value);
                        if (e.target.value !== "__others__")
                          setCategoryCustom("");
                        setAiFilledFields((p) => {
                          const n = new Set(p);
                          n.delete("category");
                          return n;
                        });
                      }}
                      className={`${aiFilledFields.has("category") ? aiFieldCls : fieldCls} pr-8 cursor-pointer`}
                    >
                      <option value="" disabled>
                        Select a category…
                      </option>
                      {DOCUMENT_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                      <option value="__others__">Custom…</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
                      <svg
                        className="h-4 w-4 text-[#9CA3AF]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {isOthers && (
                    <input
                      type="text"
                      required
                      value={categoryCustom}
                      onChange={(e) => setCategoryCustom(e.target.value)}
                      placeholder="Enter custom category…"
                      className={`${fieldCls} mt-2`}
                      autoFocus
                    />
                  )}
                </div>

                {/* ── Owner ── */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-[#374151]">
                      Owner
                    </label>
                    {!canUse("externalOwnerEmails") && (
                      <span className="text-[10px] text-[#9CA3AF]">
                        Registered users only ·{" "}
                        <button
                          type="button"
                          onClick={() =>
                            setUpgradeFeature("externalOwnerEmails")
                          }
                          className="text-[#2563EB] hover:underline"
                        >
                          Upgrade for external emails
                        </button>
                      </span>
                    )}
                  </div>
                  <OwnerChipInput
                    value={ownerEmails}
                    onChange={setOwnerEmails}
                    users={users}
                    allowExternalEmails={canUse("externalOwnerEmails")}
                    onExternalBlocked={() =>
                      setUpgradeFeature("externalOwnerEmails")
                    }
                  />
                </div>

                {/* ── Expiry Date ── */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <FieldLabel label="Expiry Date" aiKey="expiry" />
                    {isEditing && !isRenewalMode && !isDirectEditMode && (
                      <button
                        type="button"
                        onClick={() => setIsDirectEditMode(true)}
                        title="Edit expiry date"
                        className="p-1 rounded text-[#9CA3AF] hover:text-[#2563EB] hover:bg-[#EFF6FF] transition-colors"
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {isEditing && !isRenewalMode && !isDirectEditMode ? (
                    <>
                      <div className={disabledFieldCls}>
                        {expiryDate ? (
                          format(new Date(expiryDate), "MMM dd, yyyy")
                        ) : (
                          <span className="text-[#9CA3AF]">No date set</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsRenewalMode(true)}
                        className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[#E5E7EB] bg-white text-sm font-medium text-[#374151] hover:bg-[#F0FDF4] hover:text-[#15803D] hover:border-[#86EFAC] transition-colors"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        Mark as Renewal
                      </button>
                    </>
                  ) : isEditing && isDirectEditMode ? (
                    <>
                      <input
                        ref={expiryDateRef}
                        type="date"
                        id="modal-expiry"
                        value={expiryDate}
                        onChange={(e) => {
                          setExpiryDate(e.target.value);
                          setAiFilledFields((p) => {
                            const n = new Set(p);
                            n.delete("expiry");
                            return n;
                          });
                        }}
                        className={
                          aiFilledFields.has("expiry")
                            ? "mt-1 block w-full border border-[#7C3AED] rounded-md py-2 px-3 text-sm text-[#111827] bg-[#FDFCFF] focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20"
                            : "mt-1 block w-full border border-[#2563EB] rounded-md py-2 px-3 text-sm text-[#111827] bg-white focus:outline-none focus:ring-1 focus:ring-[#2563EB]"
                        }
                        autoFocus
                      />
                      <p className="mt-1.5 text-xs text-[#6B7280]">
                        Correcting the date — this will be logged as an edit,
                        not a renewal.
                      </p>
                      <button
                        type="button"
                        onClick={() => setIsDirectEditMode(false)}
                        className="mt-1 text-xs text-[#9CA3AF] hover:text-[#DC2626] transition-colors"
                      >
                        Cancel edit
                      </button>
                    </>
                  ) : isEditing && isRenewalMode ? (
                    <>
                      <input
                        ref={expiryDateRef}
                        type="date"
                        id="modal-expiry"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        className="mt-1 block w-full border border-[#86EFAC] rounded-md py-2 px-3 text-sm text-[#111827] bg-[#F0FDF4]"
                        autoFocus
                      />
                      <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-md bg-[#F0FDF4] border border-[#86EFAC]">
                        <svg
                          className="h-4 w-4 text-[#15803D] flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="text-xs text-[#15803D]">
                          Setting a new date will reset the reminder cycle and
                          log this as a renewal.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsRenewalMode(false)}
                        className="mt-1.5 text-xs text-[#9CA3AF] hover:text-[#DC2626] transition-colors"
                      >
                        Cancel renewal
                      </button>
                    </>
                  ) : (
                    /* New document — plain date input */
                    <input
                      ref={expiryDateRef}
                      type="date"
                      id="modal-expiry"
                      value={expiryDate}
                      onChange={(e) => {
                        setExpiryDate(e.target.value);
                        setAiFilledFields((p) => {
                          const n = new Set(p);
                          n.delete("expiry");
                          return n;
                        });
                      }}
                      className={
                        aiFilledFields.has("expiry") ? aiFieldCls : fieldCls
                      }
                    />
                  )}
                </div>

                {/* ── Notes ── */}
                <div>
                  <FieldLabel
                    htmlFor="modal-notes"
                    label="Notes"
                    aiKey="notes"
                  />
                  <textarea
                    id="modal-notes"
                    rows={3}
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setAiFilledFields((p) => {
                        const n = new Set(p);
                        n.delete("notes");
                        return n;
                      });
                    }}
                    placeholder="Optional notes…"
                    className={
                      aiFilledFields.has("notes") ? aiFieldCls : fieldCls
                    }
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="bg-[#F9FAFB] border-t border-[#E5E7EB] px-6 py-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-5 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm ${
                    isRenewalMode
                      ? "bg-[#15803D] hover:bg-[#166534]"
                      : "bg-[#2563EB] hover:bg-[#1D4ED8]"
                  }`}
                >
                  {loading
                    ? "Saving…"
                    : isRenewalMode
                      ? "✓ Confirm Renewal"
                      : isEditing
                        ? "Save Changes"
                        : "Create Document"}
                </button>
              </div>
            </form>
          )}

          {/* ── Tab: History ─────────────────────────────────────────────── */}
          {tab === "history" && (
            <div>
              <div className="px-6 py-5 max-h-[460px] overflow-y-auto">
                {historyLoading ? (
                  <div className="space-y-4 py-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex gap-3 animate-pulse">
                        <div className="h-7 w-7 rounded-full bg-[#E5E7EB] flex-shrink-0" />
                        <div className="flex-1 space-y-2 pt-1">
                          <div className="h-3 bg-[#E5E7EB] rounded w-1/3" />
                          <div className="h-3 bg-[#F3F4F6] rounded w-2/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <HistoryTimeline events={historyEvents} />
                )}
              </div>
              <div className="bg-[#F9FAFB] border-t border-[#E5E7EB] px-6 py-4 flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB] transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Upgrade modal ─────────────────────────────────────────────────── */}
      {upgradeFeature && (
        <UpgradeModal
          feature={
            upgradeFeature === "auditTrail"
              ? "Audit Trail"
              : upgradeFeature === "externalOwnerEmails"
                ? "External Owner Emails"
                : upgradeFeature
          }
          requiredPlan={requiredPlanFor(upgradeFeature as keyof PlanLimits)}
          onClose={() => setUpgradeFeature(null)}
        />
      )}
    </div>
  );
};

export default ItemModal;
